import * as React from 'react';
import { BASE_URL } from '../../config/environmentConfig';
import axios from 'axios';
import {
	CustomKeyReturnTypes,
	getFormattedCustomKeyFormFields, getInitialCustomKeyFormFields, IFormattedProps,
} from '../constants/custom-key/custom-key-form';
import { makeSearchParams } from '../utils/utilities';
import { errorHandler } from '../utils/newApplicationUtils';
import { successAlert } from '../components/ToastMessage';
import { MRT_PaginationState } from 'material-react-table';
import { GET } from './store-helper';
import { IApiKeys } from '../views/developers/ApiKey';

interface IUseApiClient {
	apiClientContextState: IState;
	dispatch: React.Dispatch<IAction>;
}

const ApiClientStateContext = React.createContext<IUseApiClient | undefined>(undefined);

type ApiClientProviderProps = { children: React.ReactElement }

enum apiClientActionTypes {
	FETCH_RESOURCE_LIST = 'FETCH_RESOURCE_LIST',
	SET_RESOURCE_LIST_LOADING = 'SET_RESOURCE_LIST_LOADING',
	SET_CREATING_CUSTOM_KEY_LOADING = 'SET_CREATING_CUSTOM_KEY_LOADING',
	SET_API_CLIENTS_KEYS_LOADING = 'SET_API_CLIENTS_KEYS_LOADING',
	FETCH_API_CLIENTS_KEYS_LIST = 'FETCH_API_CLIENTS_KEYS_LIST',
	SET_API_CLIENTS_KEYS_PAGINATION = 'SET_API_CLIENTS_KEYS_PAGINATION',
	FETCH_API_CLIENTS_KEYS_DETAILS = 'FETCH_API_CLIENTS_KEYS_DETAILS',
	SET_API_CLIENTS_KEYS_DETAILS_LOADING = 'SET_API_CLIENTS_KEYS_DETAILS_LOADING',
	SET_SAVING_API_CLIENTS_KEYS_DETAILS_LOADING = 'SET_SAVING_API_CLIENTS_KEYS_DETAILS_LOADING',
}

interface IAction {
	type: apiClientActionTypes;
	payload: any;
	totalItemCount?: number;
}

export interface IResourceListScope {
	claims?: any[];
	description?: string,
	displayName: string,
	name: string,
	id: string,
}

export interface IResourceList {
	description: string,
	displayName: string,
	name: string,
	scopes: IResourceListScope[],
}

export interface IApiClientCustomKeys {
	clientId: string;
	description: string;
	expiration: string;
	id: string;
	key: string;
	lastUsed: string;
	note: string;
	revokedAt: string;
	scopes: string[];
	type: string;
	value: string;
}



interface IState {
	resourceList: IResourceList[];
	isResourceListLoading: boolean;
	isCreatingCustomKey: boolean;
	apiClientKeysList: IApiClientCustomKeys[];
	isApiClientKeyListLoading: boolean;
	apiClientTotalItemCount: number;
	apiClientPageNumber: number;
	apiClientPageSize: number;
	apiClientKeysDetails: IApiClientCustomKeys;
	isApiClientKeysDetailsLoading: boolean;
	isSavingApiClientKeysDetailsLoading: boolean;
}

const apiClientReducer = (state: IState, action: IAction): IState => {
	switch (action.type) {
		case apiClientActionTypes.SET_RESOURCE_LIST_LOADING: {
			return { ...state, isResourceListLoading: action.payload };
		}
		case apiClientActionTypes.FETCH_RESOURCE_LIST: {
			return { ...state,
				resourceList: action.payload,
				isResourceListLoading: false
			};
		}
		case apiClientActionTypes.SET_CREATING_CUSTOM_KEY_LOADING: {
			return { ...state, isCreatingCustomKey: action.payload };
		}
		case apiClientActionTypes.SET_API_CLIENTS_KEYS_LOADING: {
			return { ...state, isApiClientKeyListLoading: action.payload };
		}
		case apiClientActionTypes.FETCH_API_CLIENTS_KEYS_LIST: {
			return {
				...state,
				apiClientKeysList: action.payload,
				isApiClientKeyListLoading: false,
				apiClientTotalItemCount: action.totalItemCount,
			};
		}
		case apiClientActionTypes.SET_API_CLIENTS_KEYS_PAGINATION: {
			return {
				...state,
				apiClientPageNumber: action.payload.pageNumber,
				apiClientPageSize: action.payload.pageSize,
			};
		}
		case apiClientActionTypes.SET_API_CLIENTS_KEYS_DETAILS_LOADING: {
			return { ...state, isApiClientKeysDetailsLoading: action.payload };
		}
		case apiClientActionTypes.FETCH_API_CLIENTS_KEYS_DETAILS: {
			return { ...state,
				isApiClientKeysDetailsLoading: false,
				apiClientKeysDetails: action.payload,
			};
		}
		case apiClientActionTypes.SET_SAVING_API_CLIENTS_KEYS_DETAILS_LOADING: {
			return { ...state, isSavingApiClientKeysDetailsLoading: action.payload };
		}

		default: {
			return state;
		}
	}
};

const initialState = {
	resourceList: [],
	isResourceListLoading: false,
	isCreatingCustomKey: false,
	apiClientKeysList: [],
	isApiClientKeyListLoading: false,
	apiClientTotalItemCount: 0,
	apiClientPageNumber: 0,
	apiClientPageSize: 10,
	apiClientKeysDetails: null,
	isApiClientKeysDetailsLoading: false,
	isSavingApiClientKeysDetailsLoading: false,
};

const ApiClientProvider = ({ children }: ApiClientProviderProps): JSX.Element => {
	const [apiClientContextState, dispatch] = React.useReducer(apiClientReducer, initialState);
	// NOTE: you *might* need to memoize this value
	// Learn more in http://kcd.im/optimize-context
	const value = { apiClientContextState, dispatch };
	return (
		<ApiClientStateContext.Provider value={value}>
			{children}
		</ApiClientStateContext.Provider>
	);
};

const useApiClientContext = (): IUseApiClient => {
	const context = React.useContext(ApiClientStateContext);
	if (context === undefined) {
		throw new Error('useMerchants must be used within a MerchantsProvider');
	}
	return context;
};

export const fetchApiKeys = async (mid?: string): Promise<IApiKeys[]> => {
	try {
		let url = `${BASE_URL.API_CLIENT}/ApiClients`;
		if (mid) {
			url += `?mid=${mid}`;
		}
		const data = await GET({ pathname: url });

		return data?.keys || [];
		// dispatch({ type: apiClientActionTypes.FETCH_API_KEYS, payload: data || [] });

	} catch (e) {
		console.log(e);
		// dispatch({ type: apiClientActionTypes.FETCH_FAIL, payload: e });
	}
};

export const fetchResourceList = async (dispatch: React.Dispatch<IAction>): Promise<void> => {
	dispatch({ type: apiClientActionTypes.SET_RESOURCE_LIST_LOADING, payload: true });
	try {
		const res = await axios.get(`${BASE_URL.API_CLIENT}/ApiClients/resources`);
		dispatch({ type: apiClientActionTypes.FETCH_RESOURCE_LIST, payload: res?.data || [] });
	} catch (e) {
		console.log('fetchResourceList', e);
		dispatch({ type: apiClientActionTypes.SET_RESOURCE_LIST_LOADING, payload: false });
	}
};

export const getCustomKeyQueryParams = (state: CustomKeyReturnTypes, pid?: string): { [p: string]: string } => {
	const params: { [key: string]: string } = {};

	if (state?.description) {
		params.description = state.description;
	}

	if (state?.note) {
		params.note = state.note;
	}

	if (pid) {
		params.pid = pid;
	}

	return params;
};

export const setCustomKey = async (
	dispatch: React.Dispatch<IAction>,
	formik: any,
	onSuccess: () => void,
	onError: () => void,
	pid?: string
): Promise<void> => {
	if (!formik.isValid) return;
	dispatch({ type: apiClientActionTypes.SET_CREATING_CUSTOM_KEY_LOADING, payload: true });
	try {
		const data = getFormattedCustomKeyFormFields(formik.values);
		const params = getCustomKeyQueryParams(data, pid);

		await axios.post(`${BASE_URL.API_CLIENT}/ApiClients/createApiKey`, params);
		onSuccess();
		dispatch({ type: apiClientActionTypes.SET_CREATING_CUSTOM_KEY_LOADING, payload: false });
	} catch (e) {
		onError();
		dispatch({ type: apiClientActionTypes.SET_CREATING_CUSTOM_KEY_LOADING, payload: false });
		console.log('setCustomKey', e);
	}
};

const getApiClientsKeysQueryParams = (state: IState, pid?: string): { [p: string]: string | number | boolean } => {
	const params: { [key: string]: string | number | boolean } = {};
	params.pageSize = state.apiClientPageSize;

	if (state.apiClientPageNumber || state.apiClientPageNumber === 0) {
		params.pageNumber = state.apiClientPageNumber + 1;
	}

	if (pid) {
		params.pid = pid;
	}

	return params;
};

export const fetchCustomKeyList = async (dispatch: React.Dispatch<IAction>, state: IState, pid?: string): Promise<void> => {
	dispatch({ type: apiClientActionTypes.SET_API_CLIENTS_KEYS_LOADING, payload: true });
	try {
		const params = getApiClientsKeysQueryParams(state, pid);
		const res = await axios.get(`${BASE_URL.API_CLIENT}/ApiClients/api-client-keys`, {
			params,
			paramsSerializer: params => makeSearchParams(params)
		});
		dispatch({
			type: apiClientActionTypes.FETCH_API_CLIENTS_KEYS_LIST,
			payload: res?.data?.apiClients?.rows || [],
			totalItemCount: res?.data?.apiClients?.totalItemCount
		});
	} catch (e) {
		console.log('fetchCustomKeyList', e);
	}
};

export const setCustomKeyPagination = (dispatch: React.Dispatch<IAction>, value: MRT_PaginationState): void => {
	dispatch({ type: apiClientActionTypes.SET_API_CLIENTS_KEYS_PAGINATION, payload: {
			pageNumber: value.pageIndex,
			pageSize: value.pageSize
		}});
};

export const fetchApiClientKeysDetail = async (dispatch: React.Dispatch<IAction>, keyId: string, formik: any): Promise<void> => {
	dispatch({ type: apiClientActionTypes.SET_API_CLIENTS_KEYS_DETAILS_LOADING, payload: true });
	try {
		const res = await axios.get(`${BASE_URL.API_CLIENT}/ApiClients/${keyId}/api-client-key`);
		const formikInitialValue = getInitialCustomKeyFormFields(res?.data);
		formik.setValues(formikInitialValue);
		dispatch({ type: apiClientActionTypes.FETCH_API_CLIENTS_KEYS_DETAILS, payload: res?.data || null });
	} catch (e) {
		console.log('fetchResourceList', e);
		dispatch({ type: apiClientActionTypes.FETCH_API_CLIENTS_KEYS_DETAILS, payload: null });
	}
};

export const setApiClientKeysDetailToDefault = (dispatch: React.Dispatch<IAction>): void => {
	dispatch({ type: apiClientActionTypes.FETCH_API_CLIENTS_KEYS_DETAILS, payload: null });
	dispatch({ type: apiClientActionTypes.FETCH_RESOURCE_LIST, payload: [] });
};

export const setApiClientKeysDetails = async (dispatch: React.Dispatch<IAction>, body: IFormattedProps, formik: any): Promise<void> => {
	try {
		dispatch({ type: apiClientActionTypes.SET_SAVING_API_CLIENTS_KEYS_DETAILS_LOADING, payload: true });

		await axios.put(`${BASE_URL.API_CLIENT}/ApiClients`, body);
		dispatch({ type: apiClientActionTypes.SET_SAVING_API_CLIENTS_KEYS_DETAILS_LOADING, payload: false });
		successAlert({
			title: 'Information was successfully saved!'
		});
	} catch (e) {
		dispatch({ type: apiClientActionTypes.SET_SAVING_API_CLIENTS_KEYS_DETAILS_LOADING, payload: false });
		console.log('setApiClientKeysDetails', e);
		errorHandler({ e, formik });
	}
};

export { ApiClientProvider, useApiClientContext };
